/**
 * usePrintTechnologyMismatch: the page half of `PrintTechnologyMismatchDialog`
 * (BLU-1509). Opening plays the warning buzz, fired by the scan that found
 * the mismatch, never by an effect (`useScanFeedback().triggerSuperseded`
 * pattern). The garment survives `close` so the dialog keeps its words
 * through the exit animation. Pass `open` to the page's scanner/hotkey pause
 * lists too (PRODUCTION_DESIGN §6.2).
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { playErrorBeep } from '@/lib/sounds';
import type { TechAwareScanRouting } from '@/features/production-orders/hooks/useTechAwareScanRouting';
import {
  otherPrintTechnology,
  type PrintTechnology,
} from '@/features/production-orders/utils/print-tech-routing';

interface MismatchState {
  open: boolean;
  /** The scanned garment, kept after close for the exit animation. */
  garmentId: string | null;
  /** DTG target only: the shirt needs pretreat before it is printed. */
  needsPretreat: boolean;
}

const CLOSED: MismatchState = { open: false, garmentId: null, needsPretreat: false };

export function usePrintTechnologyMismatch(
  /** What a mismatched shirt is set to: the other station's technology. */
  shirtTechnology: PrintTechnology,
  routing: Pick<TechAwareScanRouting, 'goToTech' | 'canGoToTech'>,
) {
  const { t } = useTranslation('production');
  const [state, setState] = useState<MismatchState>(CLOSED);
  const { goToTech, canGoToTech } = routing;
  const { garmentId } = state;

  const openMismatch = useCallback((id: string, needsPretreat = false) => {
    playErrorBeep();
    setState({ open: true, garmentId: id, needsPretreat });
  }, []);

  const close = useCallback(() => {
    setState((prev) => (prev.open ? { ...prev, open: false } : prev));
  }, []);

  /** The switch landed: close and say so; the page then takes the shirt on. */
  const closeSwitched = useCallback(() => {
    close();
    toast.success(t('scanRouting.switch.done', { tech: otherPrintTechnology(shirtTechnology) }));
  }, [close, t, shirtTechnology]);

  const goToStation = useCallback(() => {
    close();
    goToTech(shirtTechnology, garmentId);
  }, [close, goToTech, shirtTechnology, garmentId]);

  return {
    ...state,
    openMismatch,
    close,
    closeSwitched,
    /** Spread onto the dialog; the page adds `onSwitch`. */
    dialogProps: {
      open: state.open,
      shirtTechnology,
      needsPretreat: state.needsPretreat,
      canGoToStation: canGoToTech(shirtTechnology),
      onGoToStation: goToStation,
      onClose: close,
    },
  };
}
